import { useState } from "react";
import { motion } from "framer-motion";
import jsonLogo from "../assets/jsonparser/logo.png";
import jsonStep1 from "../assets/jsonparser/json_1.png";
import jsonStep2 from "../assets/jsonparser/json_2.png";
import jsonStep3 from "../assets/jsonparser/json_3.png";
import {
    FiCheckCircle,
    FiBookOpen,
    FiHelpCircle,
    FiChevronDown,
    FiChevronUp,
    FiList,
    FiColumns,
    FiEye,
} from "react-icons/fi";
import { useLanguage } from "../contexts/LanguageContext";

const ToolJsonParser = () => {
    const { t } = useLanguage();
    const [openIdx, setOpenIdx] = useState<number | null>(null);
    const features = [
        {
            icon: <FiList className="w-7 h-7" />,
            color: "bg-gradient-to-br from-blue-400 to-cyan-400 text-white",
            title: t("json.features.1.title"),
            desc: t("json.features.1.desc"),
        },
        {
            icon: <FiColumns className="w-7 h-7" />,
            color: "bg-gradient-to-br from-purple-400 to-pink-400 text-white",
            title: t("json.features.2.title"),
            desc: t("json.features.2.desc"),
        },
        {
            icon: <FiEye className="w-7 h-7" />,
            color: "bg-gradient-to-br from-green-400 to-teal-400 text-white",
            title: t("json.features.3.title"),
            desc: t("json.features.3.desc"),
        },
    ];

    const steps = [t("json.steps.1"), t("json.steps.2"), t("json.steps.3")];

    const stepImages = [jsonStep1, jsonStep2, jsonStep3];

    const useCases = [
        {
            icon: <FiCheckCircle className="w-4 h-4 mr-1" />,
            color: "from-blue-100 to-cyan-100 dark:from-blue-900 dark:to-cyan-900",
            text: t("json.usecases.1"),
        },
        {
            icon: <FiCheckCircle className="w-4 h-4 mr-1" />,
            color: "from-purple-100 to-pink-100 dark:from-purple-900 dark:to-pink-900",
            text: t("json.usecases.2"),
        },
        {
            icon: <FiCheckCircle className="w-4 h-4 mr-1" />,
            color: "from-green-100 to-teal-100 dark:from-green-900 dark:to-teal-900",
            text: t("json.usecases.3"),
        },
        {
            icon: <FiCheckCircle className="w-4 h-4 mr-1" />,
            color: "from-yellow-100 to-orange-100 dark:from-yellow-900 dark:to-orange-900",
            text: t("json.usecases.4"),
        },
    ];

    const faqs = [
        {
            q: t("json.faq.1.q"),
            a: t("json.faq.1.a"),
        },
        {
            q: t("json.faq.2.q"),
            a: t("json.faq.2.a"),
        },
        {
            q: t("json.faq.3.q"),
            a: t("json.faq.3.a"),
        },
    ];

    return (
        <div className="max-w-3xl mx-auto py-10 space-y-16">
            {/* 헤더 카드 */}
            <section>
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7 }}
                    className="rounded-3xl bg-gradient-to-br from-blue-500/90 via-cyan-400/80 to-purple-400/80 shadow-2xl p-12 text-center relative overflow-hidden border border-blue-200 dark:border-blue-700"
                >
                    <div className="absolute inset-0 pointer-events-none">
                        <svg
                            width="100%"
                            height="100%"
                            className="absolute left-0 top-0 opacity-10"
                            viewBox="0 0 400 100"
                        >
                            <defs>
                                <pattern
                                    id="linePattern2"
                                    width="20"
                                    height="20"
                                    patternUnits="userSpaceOnUse"
                                >
                                    <path
                                        d="M0 0L20 20ZM20 0L0 20Z"
                                        stroke="#fff"
                                        strokeWidth="0.5"
                                    />
                                </pattern>
                            </defs>
                            <rect
                                width="100%"
                                height="100%"
                                fill="url(#linePattern2)"
                            />
                        </svg>
                    </div>
                    <img
                        src={jsonLogo}
                        alt="JSON Parser 로고"
                        className="w-24 h-24 mx-auto mb-4 drop-shadow-lg bg-white rounded-2xl p-2 border-4 border-white"
                    />
                    <h1 className="text-4xl font-extrabold text-white mb-2 tracking-tight drop-shadow">
                        {t("json.header.title")}
                    </h1>
                    <p className="text-lg text-white/90 mb-2 font-medium">
                        {t("json.header.desc1")}
                    </p>
                    <p className="text-base text-white/80">
                        {t("json.header.desc2")}
                    </p>
                </motion.div>
            </section>

            {/* 주요 기능 카드형 */}
            <section>
                <motion.h2
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    viewport={{ once: true }}
                    className="text-2xl font-bold mb-8 text-center tracking-tight"
                >
                    {t("json.features.title")}
                </motion.h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-7">
                    {features.map((f, i) => (
                        <motion.div
                            key={f.title}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: i * 0.1 }}
                            viewport={{ once: true }}
                            className="rounded-2xl bg-white dark:bg-gray-900 shadow-lg p-6 flex items-start space-x-4 border border-gray-100 dark:border-gray-800 hover:scale-[1.03] transition-transform duration-200 group"
                        >
                            <div
                                className={`w-12 h-12 flex items-center justify-center rounded-xl shadow-md mb-0 mr-2 ${f.color} group-hover:scale-110 transition-transform`}
                            >
                                {f.icon}
                            </div>
                            <div>
                                <h3 className="font-semibold text-lg mb-1 text-gray-900 dark:text-white">
                                    {f.title}
                                </h3>
                                <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed">
                                    {f.desc}
                                </p>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </section>

            {/* 사용법 타임라인 */}
            <section>
                <motion.h2
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    viewport={{ once: true }}
                    className="text-2xl font-bold mb-8 text-center tracking-tight"
                >
                    {t("json.steps.title")}
                </motion.h2>
                <ol className="relative border-l-4 border-blue-200 dark:border-blue-700 ml-4 space-y-8">
                    {steps.map((step, idx) => (
                        <li key={idx} className="ml-6 relative">
                            <span className="absolute -left-7 top-1 w-6 h-6 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full flex items-center justify-center text-white font-bold shadow-lg border-2 border-white dark:border-gray-900">
                                {idx + 1}
                            </span>
                            <div
                                className={`rounded-xl shadow p-5 border border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 ${
                                    idx % 2 === 0
                                        ? "ring-1 ring-blue-100 dark:ring-blue-900"
                                        : ""
                                }`}
                            >
                                <span className="text-base text-gray-800 dark:text-gray-200 font-medium leading-relaxed">
                                    {step}
                                </span>
                                <img
                                    src={stepImages[idx]}
                                    alt={`Step ${idx + 1} 예시 이미지`}
                                    className="rounded-xl shadow my-4 border mx-auto"
                                />
                            </div>
                        </li>
                    ))}
                </ol>
            </section>

            {/* 활용 예시 뱃지형 */}
            <section>
                <motion.h2
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    viewport={{ once: true }}
                    className="text-2xl font-bold mb-8 text-center tracking-tight"
                >
                    {t("json.usecases.title")}
                </motion.h2>
                <div className="flex flex-wrap gap-4 justify-center">
                    {useCases.map((uc, i) => (
                        <span
                            key={i}
                            className={`inline-flex items-center bg-gradient-to-r ${uc.color} text-blue-700 dark:text-blue-200 px-5 py-2 rounded-full font-medium shadow text-sm border border-blue-100 dark:border-blue-900`}
                        >
                            {uc.icon}
                            {uc.text}
                        </span>
                    ))}
                </div>
            </section>

            {/* FAQ 아코디언 */}
            <section>
                <motion.h2
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    viewport={{ once: true }}
                    className="text-2xl font-bold mb-8 text-center tracking-tight"
                >
                    {t("json.faq.title")}
                </motion.h2>
                <div className="space-y-4">
                    {faqs.map((faq, idx) => (
                        <div
                            key={idx}
                            className="rounded-xl bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 shadow-lg overflow-hidden"
                        >
                            <button
                                className="w-full flex items-center justify-between px-6 py-4 text-left focus:outline-none group"
                                onClick={() =>
                                    setOpenIdx(openIdx === idx ? null : idx)
                                }
                            >
                                <span className="flex items-center space-x-2 font-semibold text-blue-700 dark:text-blue-300">
                                    <FiHelpCircle className="w-5 h-5" />
                                    <span>{faq.q}</span>
                                </span>
                                {openIdx === idx ? (
                                    <FiChevronUp className="w-5 h-5 text-blue-500 group-hover:rotate-180 transition-transform" />
                                ) : (
                                    <FiChevronDown className="w-5 h-5 text-blue-500 group-hover:rotate-180 transition-transform" />
                                )}
                            </button>
                            <div
                                className={`transition-all duration-300 ${
                                    openIdx === idx
                                        ? "max-h-40 opacity-100 py-2 px-6"
                                        : "max-h-0 opacity-0 py-0 px-6"
                                } text-gray-600 dark:text-gray-300 text-sm border-t border-blue-50 dark:border-blue-900 bg-blue-50/30 dark:bg-blue-900/20`}
                                style={{ overflow: "hidden" }}
                            >
                                {openIdx === idx && faq.a}
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* CTA */}
            <section className="text-center space-y-6">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    viewport={{ once: true }}
                >
                    <a
                        href="https://json.mewseeds.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center space-x-2 px-8 py-4 bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-xl font-semibold hover:shadow-xl hover:scale-105 transition-all duration-300"
                    >
                        <FiBookOpen className="w-5 h-5" />
                        <span>{t("json.cta")}</span>
                    </a>
                </motion.div>
            </section>
        </div>
    );
};

export default ToolJsonParser;
